import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

// Cookie-based auth: send cookies with every request. Legacy localStorage
// tokens still work as a Bearer fallback until the user re-logs in.
const client = axios.create({ baseURL: API, withCredentials: true });

const readCookie = (name) => {
  const match = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&") + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : "";
};
const UNSAFE_METHODS = new Set(["post", "put", "patch", "delete"]);

client.interceptors.request.use((config) => {
  // Legacy: attach Bearer for old sessions that still hold a token in localStorage.
  // New sessions receive httpOnly cookies and don't need this.
  const legacy = localStorage.getItem("hh_token");
  if (legacy && !config.headers.Authorization) config.headers.Authorization = `Bearer ${legacy}`;
  // Attach CSRF double-submit token on unsafe methods when we have the cookie.
  if (UNSAFE_METHODS.has((config.method || "get").toLowerCase())) {
    const csrf = readCookie("csrf_token");
    if (csrf) config.headers["X-CSRF-Token"] = csrf;
  }
  return config;
});

client.interceptors.response.use(
  (r) => r,
  async (err) => {
    // Auto-retry once on 403 CSRF errors — the /auth/me self-heal issues a
    // fresh csrf cookie on the same response, so a single retry succeeds.
    const cfg = err.config || {};
    const status = err.response?.status;
    const detail = err.response?.data?.detail || "";
    if (status === 403 && /csrf/i.test(detail) && !cfg._csrfRetried) {
      cfg._csrfRetried = true;
      try { await client.get("/auth/me"); } catch { /* ignore */ }
      const csrf = readCookie("csrf_token");
      if (csrf) cfg.headers["X-CSRF-Token"] = csrf;
      return client.request(cfg);
    }
    return Promise.reject(err);
  }
);

export default client;

export const saveAuth = (token, user) => {
  // Cookie is set by the server. We still cache the user + a legacy token so
  // pages that read `getUser()` synchronously on mount keep working.
  if (token) localStorage.setItem("hh_token", token);
  localStorage.setItem("hh_user", JSON.stringify(user));
};
export const clearAuth = () => {
  localStorage.removeItem("hh_token");
  localStorage.removeItem("hh_user");
};
export const getUser = () => {
  try { return JSON.parse(localStorage.getItem("hh_user") || "null"); } catch { return null; }
};
export const isAuthed = () => !!(localStorage.getItem("hh_token") || readCookie("csrf_token"));

// Best-effort server logout that clears cookies; local state is always cleared.
export const logout = async () => {
  try { await client.post("/auth/logout"); } catch { /* ignore network errors */ }
  clearAuth();
};

// Safely extract a human-readable error message from an axios error
// Prevents crashes when backend returns 422 validation errors (detail = array of objects)
export const errMsg = (err, fallback = "Error") => {
  const d = err?.response?.data?.detail ?? err?.response?.data ?? err?.message;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d.map((x) => {
      if (typeof x === "string") return x;
      const field = Array.isArray(x?.loc) ? x.loc.filter(p => p !== "body").join(".") : "";
      const msg = x?.msg || JSON.stringify(x);
      return field ? `${field}: ${msg}` : msg;
    }).join("; ");
  }
  if (typeof d === "object") return d.msg || d.message || JSON.stringify(d);
  return String(d);
};
